/**
 * ================ CUSTOM: Motoristpro ================
 *
 * Helpers for the Motoristpro vehicle listing flow customizations.
 * All custom logic added on top of the Sharetribe Web Template lives here or is
 * clearly fenced with "CUSTOM: Motoristpro" comments at its call sites, so that
 * template upgrades can be merged without losing these changes.
 *
 * Covered customizations:
 * 1. "Other" make/model: category nodes with id 'other' (maintained in Console)
 *    get a free-text input whose value is stored in publicData under
 *    otherCategoryLevel<N> keys.
 * 2. Auto-generated listing title: "<year> <make> <model> <trim>".
 * 3. Pinned field order in the details form (year above make/model, bodyType
 *    directly under the category selectors).
 * 4. Trim field hard cap (client-side).
 *
 * Nothing here assumes a fixed category depth or vehicle type: everything walks
 * the hosted category tree from Console config.
 */

// The id of the "Other" category nodes in Console (already used under Cars;
// the same id must be used when adding "Other" nodes to other vehicle types).
export const OTHER_CATEGORY_ID = 'other';

// Listing field keys from the hosted listing-fields asset that get special treatment.
export const YEAR_FIELD_KEY = 'year';
export const BODY_TYPE_FIELD_KEY = 'bodyType';
export const TRIM_FIELD_KEY = 'trim';

// Client-side hard cap for the trim field (Console cannot enforce this).
export const TRIM_MAX_LENGTH = 10;

// Fields that are rendered in a pinned position in EditListingDetailsForm and
// must therefore be excluded from the generic AddListingFields list.
export const PINNED_LISTING_FIELD_KEYS = [YEAR_FIELD_KEY, BODY_TYPE_FIELD_KEY];

/**
 * Transaction processes whose wizard merges the Photos upload into the Details
 * step (no separate Photos tab; wizard order Details(+Photos) > Pricing > Location).
 * All Motoristpro listing types use the inquiry process.
 */
export const PHOTOS_MERGED_PROCESS_NAMES = ['default-inquiry'];

/**
 * Is the Photos step merged into the Details step for the given process?
 *
 * @param {String} processName transaction process name (e.g. 'default-inquiry')
 * @param {Boolean} requiresListingImage whether the listing type requires images
 *   (listing types without images use the Style tab and keep stock behavior)
 */
export const isPhotosMergedIntoDetails = (processName, requiresListingImage = true) =>
  !!requiresListingImage && PHOTOS_MERGED_PROCESS_NAMES.includes(processName);

/**
 * Form field name / publicData key for the free-text value typed when the
 * "Other" category option is selected on a given level.
 *
 * NOTE: this key must NOT start with the category prefix ('categoryLevel'),
 * because the template's FieldSelectCategory counts selected category levels
 * with Object.keys(values).filter(key => key.startsWith(prefix)).
 */
export const otherCategoryKey = level => `otherCategoryLevel${level}`;

// Max depth of the given category tree (e.g. 3 for vehicle type > make > model).
export const getMaxCategoryDepth = (categories = []) =>
  categories.reduce(
    (max, category) => Math.max(max, 1 + getMaxCategoryDepth(category.subcategories)),
    0
  );

/**
 * Walk the category tree along the selections stored in `data`
 * (categoryLevel1, categoryLevel2, ...) and return one entry per selected level:
 * { level, selectedId, isOther, name }.
 *
 * - `name` is the category's display name from config, or the typed free-text
 *   value (otherCategoryLevel<N>) when the "Other" node is selected.
 * - If an "Other" node is a leaf but its sibling categories have subcategories
 *   (e.g. the "Other" make under Cars has no model list while BMW does), a
 *   "phantom" entry for the next level is included when a typed value exists,
 *   so that an "Other" make can still carry a typed model.
 *
 * Works against both form values and listing publicData, since both use the
 * same keys.
 */
export const getSelectedCategoryLevels = (data, prefix, categories) => {
  const entries = [];
  const walk = (options, level) => {
    const selectedId = data?.[`${prefix}${level}`];
    const config = options?.find(category => category.id === selectedId);
    if (!config) {
      return;
    }
    const isOther = selectedId === OTHER_CATEGORY_ID;
    const typedValue = data?.[otherCategoryKey(level)];
    const typedName = typeof typedValue === 'string' ? typedValue.trim() : null;
    entries.push({
      level,
      selectedId,
      isOther,
      name: isOther && typedName ? typedName : config.name,
    });

    const subcategories = config.subcategories || [];
    if (subcategories.length > 0) {
      walk(subcategories, level + 1);
    } else if (isOther && options.some(option => option.subcategories?.length > 0)) {
      // Phantom next level under a leaf "Other" node (see JSDoc above).
      const typedNext = data?.[otherCategoryKey(level + 1)];
      const typedNextName = typeof typedNext === 'string' ? typedNext.trim() : null;
      if (typedNextName) {
        entries.push({
          level: level + 1,
          selectedId: OTHER_CATEGORY_ID,
          isOther: true,
          name: typedNextName,
        });
      }
    }
  };
  walk(categories, 1);
  return entries;
};

/**
 * Should the "Other" free-text input be shown for the phantom next level?
 * True when the selected node on the current level is a leaf "Other" node while
 * its siblings have subcategories (e.g. "Other" make => typed model input).
 */
export const showPhantomOtherLevel = (selectedId, selectedConfig, currentOptions) =>
  selectedId === OTHER_CATEGORY_ID &&
  !!selectedConfig &&
  !(selectedConfig.subcategories?.length > 0) &&
  !!currentOptions?.some(option => option.subcategories?.length > 0);

/**
 * Pick typed "Other" values from form values / publicData for the currently
 * selected categories. Returns e.g. { otherCategoryLevel2: 'Lada' } and only
 * contains keys for levels where the "Other" node is actually selected
 * (including the phantom level). Empty/whitespace values become null.
 */
export const pickOtherCategoryValues = (data, prefix, categories) => {
  const values = {};
  const walk = (options, level) => {
    const selectedId = data?.[`${prefix}${level}`];
    const config = options?.find(category => category.id === selectedId);
    if (!config) {
      return;
    }
    if (selectedId === OTHER_CATEGORY_ID) {
      const typed = data?.[otherCategoryKey(level)];
      values[otherCategoryKey(level)] = typeof typed === 'string' && typed.trim() ? typed.trim() : null;
    }
    const subcategories = config.subcategories || [];
    if (subcategories.length > 0) {
      walk(subcategories, level + 1);
    } else if (showPhantomOtherLevel(selectedId, config, options)) {
      const typed = data?.[otherCategoryKey(level + 1)];
      values[otherCategoryKey(level + 1)] =
        typeof typed === 'string' && typed.trim() ? typed.trim() : null;
    }
  };
  walk(categories, 1);
  return values;
};

/**
 * Explicit nulls for every possible otherCategoryLevel<N> key (up to tree depth
 * + 1 to cover the phantom level). Spread this BEFORE pickOtherCategoryValues
 * in publicData on submit, so that stale typed values are cleared when the
 * seller moves away from an "Other" selection. Mirrors how the template clears
 * categoryLevel keys.
 */
export const clearedOtherCategoryValues = categories => {
  const maxDepth = getMaxCategoryDepth(categories) + 1;
  const cleared = {};
  for (let level = 1; level <= maxDepth; level++) {
    cleared[otherCategoryKey(level)] = null;
  }
  return cleared;
};

/**
 * Build the auto-generated listing title: "<year> <make> <model> <trim>",
 * e.g. "2007 Audi A2 S SPEC". Level 1 (vehicle type) is not part of the title.
 * Typed "Other" values are used in place of category names where selected.
 * Missing parts are simply skipped.
 *
 * @param {Object} params
 * @param {String} params.year value of the year listing field
 * @param {String} params.trim value of the trim listing field
 * @param {Object} params.data form values or publicData with categoryLevel* keys
 * @param {String} params.prefix category key prefix (config.categoryConfiguration.key)
 * @param {Array} params.categories hosted category tree
 * @param {Number} params.maxLength max length of the generated title
 */
export const buildListingTitle = ({ year, trim, data, prefix, categories, maxLength = 60 }) => {
  const categoryNames = getSelectedCategoryLevels(data, prefix, categories)
    .filter(entry => entry.level >= 2)
    .map(entry => entry.name);
  const parts = [year, ...categoryNames, typeof trim === 'string' ? trim.trim() : null];
  return parts
    .filter(part => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .replace(/\s+/g, ' ')
    .slice(0, maxLength)
    .trim();
};
